import React, { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { SettingsDrawer } from "@components-shared/mobile/settings-drawer";
import { Button } from "@components/buttons";
import CountryIcon from "@components/icons/CountryIcon";
import {
	IEventPlayer,
	IPlayerStatGroup,
	PlayerSortConfig,
	PlayersByTeamFilter,
} from "@data-types/sport-stats/SportStatsTypes";
import { EventContext, LookupContext } from "@gp/components";
import LivePlayerStatisticsStore from "@state/stores/offer/statistics/LivePlayerStatisticsStore";
import classNames from "classnames";
import classnames from "classnames";
import { observer } from "mobx-react";

export default observer(function GroupedPlayerStats(props: {
	store: LivePlayerStatisticsStore;
	isSettingsDrawerOpen: boolean;
	onClose: (value: boolean) => void;
}) {
	const [isExpandedHomeHeader, setExpandHomeHeader] = useState(true);
	const [isExpandedAwayHeader, setExpandAwayHeader] = useState(true);

	const sortConfig: PlayerSortConfig = {
		key: "position",
		direction: "asc",
	};

	const event = useContext(EventContext);
	const { teams } = useContext(LookupContext);

	const homeTeam = teams.get(event.teamOneId || "");
	const awayTeam = teams.get(event.teamTwoId || "");

	if (homeTeam == null || awayTeam == null) {
		console.error("Expected teams, got one or more null.");
		return null;
	}

	const activeGroup = props.store.playerStatsGroups.find(
		(group) => group.key === props.store.playerFilter
	);

	const teamConfigs = [
		{
			key: "home" as PlayersByTeamFilter,
			isExpandedHeader: isExpandedHomeHeader,
			setExpandHeader: setExpandHomeHeader,
			teamName: homeTeam.name,
		},
		{
			key: "away" as PlayersByTeamFilter,
			isExpandedHeader: isExpandedAwayHeader,
			setExpandHeader: setExpandAwayHeader,
			teamName: awayTeam.name,
		},
	];

	const filteredConfigs =
		props.store.activeTab === "all"
			? teamConfigs
			: teamConfigs.filter((c) => c.key === props.store.activeTab);

	const teamsConfig = filteredConfigs.map((config) => ({
		...config,
		players: props.store.filterPlayers(
			config.key,
			props.store.playerFilter,
			sortConfig
		),
	}));

	return (
		<>
			{teamsConfig.map((config) => (
				<div
					key={config.teamName}
					className={classNames(
						"offer offer--additional offer--additional--stats",
						{
							"is-collapsed": !config.isExpandedHeader,
							"is-expanded": config.isExpandedHeader,
						}
					)}
				>
					<GroupedPlayerStatsHeader
						isExpanded={config.isExpandedHeader}
						onSetExpand={config.setExpandHeader}
						title={config.teamName}
					/>

					{config.players.length > 0 && config.isExpandedHeader && (
						<div className="offer__body offer--additional__body u-padd--reset">
							{config.players.map(
								(player: IEventPlayer) =>
									activeGroup && (
										<PlayerStatsGroup
											isExpandedHeader={
												config.isExpandedHeader
											}
											key={player.name}
											player={player}
											statsGroup={activeGroup}
										/>
									)
							)}
						</div>
					)}
				</div>
			))}

			<GroupedPlayerStatsDrawer
				store={props.store}
				isSettingsDrawerOpen={props.isSettingsDrawerOpen}
				onClose={props.onClose}
				setExpandHomeHeader={setExpandHomeHeader}
				setExpandAwayHeader={setExpandAwayHeader}
			/>
		</>
	);
});

function PlayerStatsGroup(props: {
	player: IEventPlayer;
	statsGroup: IPlayerStatGroup;
	isExpandedHeader: boolean;
}) {
	const [isExpanded, setExpand] = useState(false);

	if (!props.isExpandedHeader) {
		return null;
	}

	return (
		<div
			className={classnames("card card--raised card--stats", {
				"is-collapsed": !props.isExpandedHeader,
				"is-expanded": props.isExpandedHeader,
			})}
		>
			<StatsGroupHeader
				isExpanded={isExpanded}
				onSetExpand={setExpand}
				statsGroup={props.statsGroup}
				player={props.player}
			/>

			<StatsGroupBody
				isExpanded={isExpanded}
				statsGroup={props.statsGroup}
				player={props.player}
			/>
		</div>
	);
}

function StatsGroupHeader(props: {
	isExpanded?: boolean;
	onSetExpand: (newValue: boolean) => void;
	statsGroup: IPlayerStatGroup;
	player: IEventPlayer;
}) {
	const { isExpanded, onSetExpand, player } = props;

	const toggleIconClass = classNames("u-icon u-icon--tny", {
		"u-icon--caret--down": isExpanded,
		"u-icon--caret--right": !isExpanded,
	});

	const playerRating = +player.rating.toFixed(1);

	const counterClassName = classNames(
		"statistics--player__tag tag tag--med tag--rating",
		{
			"tag--rating--bottom": playerRating > 0 && playerRating <= 6,
			"tag--rating--low": playerRating > 6 && playerRating <= 6.5,
			"tag--rating--medium": playerRating > 6.5 && playerRating <= 7,
			"tag--rating--good": playerRating > 7 && playerRating <= 8,
			"tag--rating--high": playerRating > 8 && playerRating <= 9,
			"tag--rating--top": playerRating > 9 && playerRating <= 10,
			"tag--rating--none": !player.rating,
		}
	);

	const playerCard = (yellowCard?: number, redCard?: number) => {
		if (yellowCard === 1 && redCard !== 1) {
			return (
				<i className="u-icon u-icon--xsml u-icon--card u-icon--card--rotate u-icon--card--yellow" />
			);
		} else if (redCard === 1 || (yellowCard === 1 && redCard === 1)) {
			return (
				<i className="u-icon u-icon--xsml u-icon--card u-icon--card--rotate u-icon--card--red" />
			);
		} else if (yellowCard === 2) {
			return (
				<i className="u-icon u-icon--xsml u-icon--card u-icon--card--combined" />
			);
		}
	};

	const playerAssists = Number(player.statistics?.summary?.assists ?? 0);

	return (
		<div
			className={classNames("card__header card--stats__header", {
				"is-collapsed": !isExpanded,
				"is-expanded": isExpanded,
			})}
			onClick={() => onSetExpand(!isExpanded)}
			role="button"
			aria-expanded={isExpanded}
			aria-controls={"statsGroupBody--" + player.jerseyNumber}
		>
			<div className="statistics--player__jersey">
				<div className="statistics--player__number">
					{player.jerseyNumber}
				</div>
			</div>

			<div className="statistics--player__title">
				<div className="statistics--player__team">
					<div className="statistics--player__position">
						{player.position}
					</div>
					<div className="statistics--player__name">
						{player.name}
					</div>
					<CountryIcon
						flagCode={player.country}
						className="statistics--player__country u-icon--xsml"
					/>
				</div>

				<div className="statistics--player__info">
					{player.substitute ? (
						<i className="u-icon u-icon--xsml u-icon--substitute" />
					) : null}

					{player.goals > 0 && (
						<div className="statistics--player__goal">
							<i className="u-icon u-icon--xsml u-icon--goal" />
							<span>{player.goals > 1 ? player.goals : ""}</span>
						</div>
					)}

					{playerAssists > 0 && (
						<div className="statistics--player__goal">
							<i className="u-icon u-icon--xsml u-icon--assists" />
							<span>
								{playerAssists > 1 ? playerAssists : ""}
							</span>
						</div>
					)}

					{player.ownGoals > 0 && (
						<div className="statistics--player__goal">
							<i className="u-icon u-icon--xsml u-icon--own-goal" />
							<span>
								{player.ownGoals > 1 ? player.ownGoals : ""}
							</span>
						</div>
					)}

					{playerCard(player.yellowCard, player.redCard)}
				</div>
			</div>

			<span className={counterClassName}>
				{player.rating ? player.rating.toFixed(1) : "-"}
			</span>

			<i className={toggleIconClass} />
		</div>
	);
}

function StatsGroupBody(props: {
	statsGroup: IPlayerStatGroup;
	isExpanded: boolean;
	player: IEventPlayer;
}) {
	const { isExpanded, statsGroup, player } = props;

	const { t } = useTranslation();

	if (!isExpanded) {
		return null;
	}

	return (
		<div
			id={"statsGroupBody--" + player.jerseyNumber}
			className={classnames("card--stats__body", {
				"is-collapsed": !isExpanded,
				"is-expanded": isExpanded,
			})}
		>
			{statsGroup.key !== "goalkeeper" && (
				<div key={player.position} className="statistics__row">
					<div className="statistics__col u-type--left">
						{t(
							"EVENT.STATISTICS.PLAYER_STATS.PLAYER_STAT_TITLES.POSITION"
						)}
					</div>
					<div className="statistics__col u-type--wgt--medium">
						{player.position}
					</div>
				</div>
			)}

			{statsGroup.data.map((stat, index) => (
				<div key={index} className="statistics__row">
					<div className="statistics__col u-type--left">
						{t(stat.title)}
					</div>
					<div className="statistics__col u-type--wgt--medium">
						{player.statistics[statsGroup.key]?.[stat.key] || "0"}
					</div>
				</div>
			))}

		</div>
	);
}

function GroupedPlayerStatsDrawer(props: {
	store: LivePlayerStatisticsStore;
	isSettingsDrawerOpen: boolean;
	onClose: (value: boolean) => void;
	setExpandHomeHeader: (value: boolean) => void;
	setExpandAwayHeader: (value: boolean) => void;
}) {
	const { t } = useTranslation();
	const [activeTab, setActiveTab] = useState<PlayersByTeamFilter>(
		props.store.activeTab
	);
	const [playerFilter, setPlayerFilter] = useState<string>(
		props.store.playerFilter
	);

	const event = useContext(EventContext);
	const { teams } = useContext(LookupContext);

	const homeTeam = teams.get(event.teamOneId || "");
	const awayTeam = teams.get(event.teamTwoId || "");

	if (homeTeam == null || awayTeam == null) {
		console.error("Expected teams, got one or more null.");
		return null;
	}
	return (
		<SettingsDrawer
			onClose={() => {
				setActiveTab(props.store.activeTab);
				setPlayerFilter(props.store.playerFilter);
				props.onClose(false);
			}}
			isOpen={props.isSettingsDrawerOpen}
		>
			<div className="drawer__header">
				<div className="drawer__heading">
					{t("EVENT.HEADER.PLAYER_STATS")}
				</div>
			</div>

			<div className="drawer__body">
				<div className="drawer__section">
					<div className="drawer__label label u-mar--bottom--reset">
						{t("EVENT.HEADER.SELECT_TEAM")}
					</div>

					{props.store.availablePlayerTabs &&
						props.store.availablePlayerTabs.map((tab) => (
							<div
								key={tab.key}
								className="drawer__option input--radio"
							>
								<input
									type="radio"
									id={`radio-tab-${tab.key}`}
									name="playerTab"
									value={tab.key}
									checked={activeTab === tab.key}
									onChange={() =>
										setActiveTab(
											tab.key as PlayersByTeamFilter
										)
									}
									className="input--radio__box"
								/>
								<label
									htmlFor={`radio-tab-${tab.key}`}
									className="input--radio__label"
								>
									{t(tab.title!)}
								</label>
							</div>
						))}
				</div>

				<div className="drawer__section">
					<div className="drawer__label label u-mar--bottom--reset">
						{t("EVENT.HEADER.SELECT_PLAYER_STATS")}
					</div>

					{props.store.playerStatsGroups.map(
						(group) =>
							group.data &&
							group.data.length > 0 && (
								<div
									key={group.key}
									className="drawer__option input--radio"
								>
									<input
										type="radio"
										id={`radio-${group.key}`}
										name="playerStatsGroup"
										value={group.key}
										checked={playerFilter === group.key}
										onChange={() =>
											setPlayerFilter(group.key)
										}
										className="input--radio__box"
									/>
									<label
										htmlFor={`radio-${group.key}`}
										className="input--radio__label"
									>
										{t(group.title)}
									</label>
								</div>
							)
					)}
				</div>
			</div>

			<div className="drawer__footer">
				<Button
					btnText={t("ACC_STATEMENT.BET_DETAILS.CLOSE_BTN")}
					onClick={() => {
						setActiveTab(props.store.activeTab);
						setPlayerFilter(props.store.playerFilter);
						props.onClose(false);
					}}
					className="btn--med btn--block btn--primary--inverted drawer__action--secondary"
				/>
				<Button
					btnText={t("mybets:PAGE.FILTER_FORM.SUBMIT_BUTTON")}
					onClick={() => {
						props.store.setActiveTab(activeTab);
						props.store.setPlayerFilter(playerFilter);
						props.setExpandHomeHeader(true);
						props.setExpandAwayHeader(true);
						props.onClose(false);
					}}
					className="btn--med btn--block btn--accent drawer__action--primary"
				/>
			</div>
		</SettingsDrawer>
	);
}

function GroupedPlayerStatsHeader(props: {
	isExpanded?: boolean;
	onSetExpand: (newValue: boolean) => void;
	title: string;
}) {
	const { t } = useTranslation();

	return (
		<h3
			className={classNames(
				"offer__header offer--additional__head u-padd--x--reset",
				{
					"is-collapsed": !props.isExpanded,
					"is-expanded": props.isExpanded,
				}
			)}
		>
			<button
				type="button"
				onClick={() => props.onSetExpand(!props.isExpanded)}
				className={classNames(
					"offer__toggle offer--additional__head__toggle btn btn--base btn--block",
					{
						"is-collapsed": !props.isExpanded,
						"is-expanded": props.isExpanded,
					}
				)}
			>
				<span className="offer__title offer--additional__head__title">
					{t(props.title)}
				</span>
				<span className="offer__actions">
					<span className="offer__actions__btn btn btn--icon btn--square btn--square--xtny btn--ghost">
						<i
							className={
								"btn__icon u-icon u-icon--tny" +
								(!props.isExpanded
									? " u-icon--caret--right"
									: " u-icon--caret--up")
							}
						/>
					</span>
				</span>
			</button>
		</h3>
	);
}
